import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { AlcanceI } from "interfaces/Alcance";
import { EstadoValidacionNacionalI } from "interfaces/EstadoValidacionNacional";
import { EstadoI } from "interfaces/general";
import { NivelI } from "interfaces/Nivel";
import { RolI } from "interfaces/Rol";
import { TipoEfectoI } from "interfaces/TipoEfecto";
import { TipoNormativaI } from "interfaces/TipoNormativa";
import {
  getAlcances,
  getEstados,
  getRoles,
  getTiposEfecto,
  getTiposResoluciones,
} from "services/general";
import { getNiveles } from "services/niveles";
import { getEstadoValidezNacional } from "services/validezNacional";

export interface appDataState {
  tiposNormativa: TipoNormativaI[] | undefined;
  tiposEfecto: TipoEfectoI[] | undefined;
  roles: RolI[] | undefined;
  estados: EstadoI[] | undefined;
  estadosValidacionNacional: EstadoValidacionNacionalI[] | undefined;
  niveles: NivelI[] | undefined;
  alcances: AlcanceI[] | undefined;

  loadingAppData: {
    tiposNormativa: boolean;
    tiposEfecto: boolean;
    roles: boolean;
    estados: boolean;
    estadosValidacionNacional: boolean;
    niveles: boolean;
    alcances: boolean;
  };

  errorAppData: {
    tiposNormativa: undefined | string;
    tiposEfecto: undefined | string;
    roles: undefined | string;
    estados: undefined | string;
    estadosValidacionNacional: undefined | string;
    niveles: undefined | string;
    alcances: undefined | string;
  };
}

const defaultState: appDataState = {
  tiposNormativa: undefined,
  tiposEfecto: undefined,
  roles: undefined,
  estados: undefined,
  estadosValidacionNacional: undefined,
  niveles: undefined,
  alcances: undefined,

  loadingAppData: {
    tiposNormativa: false,
    tiposEfecto: false,
    roles: false,
    estados: false,
    estadosValidacionNacional: false,
    niveles: false,
    alcances: false,
  },

  errorAppData: {
    tiposNormativa: undefined,
    tiposEfecto: undefined,
    roles: undefined,
    estados: undefined,
    estadosValidacionNacional: undefined,
    niveles: undefined,
    alcances: undefined,
  },
};

const initialState: appDataState = defaultState;

export const fetchTiposResoluciones = createAsyncThunk<TipoNormativaI[]>(
  "appData/fetchTiposResoluciones",
  async () => {
    return await getTiposResoluciones({});
  },
);

export const fetchTiposEfecto = createAsyncThunk<TipoEfectoI[]>(
  "appData/fetchTiposEfecto",
  async () => {
    return await getTiposEfecto({});
  },
);

export const fetchRoles = createAsyncThunk<RolI[]>(
  "appData/fetchRoles",
  async () => {
    return await getRoles({});
  },
);

export const fetchEstados = createAsyncThunk<EstadoI[]>(
  "appData/fetchEstados",
  async () => {
    return await getEstados({});
  },
);

export const fetchNiveles = createAsyncThunk<NivelI[]>(
  "appData/fetchNiveles",
  async () => {
    return await getNiveles({});
  },
);

export const fetchAlcances = createAsyncThunk<AlcanceI[]>(
  "appData/fetchAlcances",
  async () => {
    return await getAlcances({});
  },
);

export const fetchEstadosValidacionNacional = createAsyncThunk<
  EstadoValidacionNacionalI[]
>("appData/fetchEstadosValidacionNacional", async () => {
  return await getEstadoValidezNacional({});
});

export const appDataSlice = createSlice({
  name: "appData",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchTiposResoluciones.pending, (state) => {
        state.loadingAppData.tiposNormativa = true;
        state.errorAppData.tiposNormativa = undefined;
      })
      .addCase(
        fetchTiposResoluciones.fulfilled,
        (state, action: PayloadAction<TipoNormativaI[]>) => {
          state.loadingAppData.tiposNormativa = false;
          state.tiposNormativa = action.payload;
        },
      )
      .addCase(fetchTiposResoluciones.rejected, (state, action) => {
        state.loadingAppData.tiposNormativa = false;
        state.errorAppData.tiposNormativa = action.error.message;
      });

    builder
      .addCase(fetchTiposEfecto.pending, (state) => {
        state.loadingAppData.tiposEfecto = true;
        state.errorAppData.tiposEfecto = undefined;
      })
      .addCase(
        fetchTiposEfecto.fulfilled,
        (state, action: PayloadAction<TipoEfectoI[]>) => {
          state.loadingAppData.tiposEfecto = false;
          state.tiposEfecto = action.payload;
        },
      )
      .addCase(fetchTiposEfecto.rejected, (state, action) => {
        state.loadingAppData.tiposEfecto = false;
        state.errorAppData.tiposEfecto = action.error.message;
      });

    builder
      .addCase(fetchRoles.pending, (state) => {
        state.loadingAppData.roles = true;
        state.errorAppData.roles = undefined;
      })
      .addCase(fetchRoles.fulfilled, (state, action: PayloadAction<RolI[]>) => {
        state.loadingAppData.roles = false;
        state.roles = action.payload;
      })
      .addCase(fetchRoles.rejected, (state, action) => {
        state.loadingAppData.roles = false;
        state.errorAppData.roles = action.error.message;
      });

    builder
      .addCase(fetchEstados.pending, (state) => {
        state.loadingAppData.estados = true;
        state.errorAppData.estados = undefined;
      })
      .addCase(
        fetchEstados.fulfilled,
        (state, action: PayloadAction<EstadoI[]>) => {
          state.loadingAppData.estados = false;
          state.estados = action.payload;
        },
      )
      .addCase(fetchEstados.rejected, (state, action) => {
        state.loadingAppData.estados = false;
        state.errorAppData.estados = action.error.message;
      });

    builder
      .addCase(fetchNiveles.pending, (state) => {
        state.loadingAppData.niveles = true;
        state.errorAppData.niveles = undefined;
      })
      .addCase(
        fetchNiveles.fulfilled,
        (state, action: PayloadAction<NivelI[]>) => {
          state.loadingAppData.niveles = false;
          state.niveles = action.payload;
        },
      )
      .addCase(fetchNiveles.rejected, (state, action) => {
        state.loadingAppData.niveles = false;
        state.errorAppData.niveles = action.error.message;
      });

    builder
      .addCase(fetchAlcances.pending, (state) => {
        state.loadingAppData.alcances = true;
        state.errorAppData.alcances = undefined;
      })
      .addCase(
        fetchAlcances.fulfilled,
        (state, action: PayloadAction<AlcanceI[]>) => {
          state.loadingAppData.alcances = false;
          state.alcances = action.payload;
        },
      )
      .addCase(fetchAlcances.rejected, (state, action) => {
        state.loadingAppData.alcances = false;
        state.errorAppData.alcances = action.error.message;
      });

    builder
      .addCase(fetchEstadosValidacionNacional.pending, (state) => {
        state.loadingAppData.estadosValidacionNacional = true;
        state.errorAppData.estadosValidacionNacional = undefined;
      })
      .addCase(
        fetchEstadosValidacionNacional.fulfilled,
        (state, action: PayloadAction<EstadoValidacionNacionalI[]>) => {
          state.loadingAppData.estadosValidacionNacional = false;
          state.estadosValidacionNacional = action.payload;
        },
      )
      .addCase(fetchEstadosValidacionNacional.rejected, (state, action) => {
        state.loadingAppData.estadosValidacionNacional = false;
        state.errorAppData.estadosValidacionNacional = action.error.message;
      });
  },
});

export default appDataSlice.reducer;
